import React, { useEffect, useState } from 'react';
import Icon from '@sabre/spark-react-core/icon';
import Panel from '@sabre/spark-react-core/panel';
import Tooltip from '@sabre/spark-react-core/tooltip';
import { isEmpty, map, sortBy, uniq } from 'lodash';
import { object } from 'prop-types';
import useApi from '../../api-wrapper/use-api.js';
import { URL_HEP_ANALYST_POS_MAPPING } from '../../end-points.js';
import LayoutTwoVerticalGrids from '../../ui-components/layouts/layout-two-vertical-grids.jsx';
import Loader from '../../ui-components/loader/loader.jsx';
import { getClonedObject } from '../../utils/common-util.js';
import {
   DASHBOARD_TOP_TABS_VALUE,
   IS_FARE_CHANGES_SUMMARY_FROM_ATHENA_KEY,
   SIZE,
} from '../../utils/constants.js';
import { getFeaturesHepProperty } from '../shared/app-configs-util/app-configs-util.js';
import {
   AGGREGATION_DATES,
   DASHBOARD_SEARCH_CRITERIA_KEYS,
   DEFAULT_FARE_TYPE,
   BASE_FARE,
} from '../shared/charts-and-grids/charts-util.js';
import SearchCriteriaFilters from '../shared/search-criteria-filters/search-criteria-filters.jsx';
import SearchCriteriaModal from '../shared/search-criteria-modal/search-criteria-modal.jsx';
import CompetitionContainer from './competition-container.jsx';
import DashboardTopTabs from './dashboard-top-tabs.jsx';
import { getDashboardTopTabs } from './dashboard-util.js';
import RecommendationContainer from './recommendation-container.jsx';

const Dashboard = ({ App }) => {
   const [isOpenModal, setIsOpenModal] = useState(true);
   const [isShowSearchLoader, setIsShowSearchLoader] = useState(0);
   const [isShowNotification, setIsShowNotification] = useState(false);
   const [isErrorDateOutOfRange, setIsErrorDateOutOfRange] = useState(false);
   const [searchCriteriaPayload, setSearchCriteriaPayload] = useState({});
   const [commonFilters, setCommonFilters] = useState(null);
   const [filterByPerCategorySummary, setFilterByPerCategorySummary] = useState(
      {}
   );
   const [filterBy, setFilterBy] = useState({});
   const [onChangeFilterName, setOnChangeFilterName] = useState('');
   const [recommCompetitor, setRecommCompetitor] = useState(
      DASHBOARD_TOP_TABS_VALUE.RECOMMENDATION
   );
   const [reasonsFilter, setReasonsFilter] = useState({});
   const [analystData, fetchAnalystData] = useApi({});
   const dashboardTabConfig = getDashboardTopTabs();
   const LOADER_THRESHOLD = 2; // TODO: value set to 2 as we know we are going to hit 2 APIs. Can be configured dynamically.

   const isFareChangesSummaryFromAthena = () => {
      const isFareChangesSummaryFromAthena = getFeaturesHepProperty(
         App,
         IS_FARE_CHANGES_SUMMARY_FROM_ATHENA_KEY
      );
      return !(
         !isEmpty(isFareChangesSummaryFromAthena) &&
         !isFareChangesSummaryFromAthena.enable
      );
   };

   const resetFilters = () => {
      setCommonFilters(null);
      setFilterByPerCategorySummary({});
      setFilterBy({});
      setReasonsFilter({});
      setOnChangeFilterName('');
   };

   const handleSearchModalOpenOrClose = (flag) => {
      if (isOpenModal) {
         setIsOpenModal(flag);
      }
   };

   const handleSearchCriteriaButtonOnClick = (
      searchPayload,
      aggregationFromDate,
      aggregationToDate
   ) => {
      setIsShowSearchLoader(LOADER_THRESHOLD);
      resetFilters();
      setIsShowNotification(false);
      setIsErrorDateOutOfRange(false);
      setSearchCriteriaPayload({
         [DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE]: {
            [AGGREGATION_DATES.AGGREGATION_FROM_DATE]: aggregationFromDate,
            [AGGREGATION_DATES.AGGREGATION_TO_DATE]: aggregationToDate,
         },
         filterBy: searchPayload,
      });
   };

   const handleDashboardTopTabOnClick = (selectedValue) => {
      setRecommCompetitor(selectedValue);
      resetFilters();
      setSearchCriteriaPayload({});
      setIsShowNotification(false);
      setIsOpenModal(true);
   };

   const handleSearchCriteriaIconOnClick = (value) => {
      setIsOpenModal(value);
      setIsShowNotification(false);
   };

   const handleDashboardFiltersOnChange = (
      selectedValues,
      filterName,
      el,
      isUserEvent
   ) => {
      let copyOfFilterBy = getClonedObject(filterBy);
      if (DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_MARKET_TIER === filterName) {
         const elValueIndex = copyOfFilterBy[filterName].indexOf(el.value);
         if (isEmpty(selectedValues)) {
            copyOfFilterBy[filterName] = [
               ...copyOfFilterBy[filterName].slice(0, elValueIndex),
               ...copyOfFilterBy[filterName].slice(elValueIndex + 1),
            ];
         } else if (copyOfFilterBy[filterName].indexOf(selectedValues) === -1) {
            copyOfFilterBy[filterName] = selectedValues;
         }
      } else if (
         DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE === filterName
      ) {
         copyOfFilterBy[filterName] = {
            [AGGREGATION_DATES.AGGREGATION_FROM_DATE]: selectedValues.start,
            [AGGREGATION_DATES.AGGREGATION_TO_DATE]: selectedValues.end,
         };
      } else if (
         DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_COMPONENT === filterName
      ) {
         copyOfFilterBy[filterName] = selectedValues;
      } else {
         copyOfFilterBy = {
            ...filterBy,
            [filterName]: map(
               selectedValues,
               (selectedValue) => selectedValue.value
            ),
         };
      }

      // TODO: selection and deselection of filter should dynamically pic the source data for filtering (filtered or raw data)
      // if(isUserEvent) {
      //     setOnChangeFilterName(filterName);
      // }

      setFilterBy(copyOfFilterBy);
   };

   const combineFiltersOfAllCategories = (
      availableFilters,
      categorySummaryKey
   ) => {
      // TODO: code refinement needed to increase performance
      let combinedFilters = {};
      if (isEmpty(filterByPerCategorySummary)) {
         combinedFilters = availableFilters;
      } else {
         map(filterByPerCategorySummary, (eachCaterogyFilterBy, key) => {
            if (key !== categorySummaryKey) {
               map(eachCaterogyFilterBy, (eachFilterBy, eachKey) => {
                  if (
                     DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_AGGREGATION_DATE ===
                     eachKey
                  ) {
                     combinedFilters[eachKey] = eachFilterBy;
                  } else {
                     combinedFilters[eachKey] = isEmpty(
                        availableFilters[eachKey]
                     )
                        ? eachFilterBy
                        : uniq([...eachFilterBy, ...availableFilters[eachKey]]);
                  }
               });
            }
         });
      }

      return combinedFilters;
   };

   const getDefaultFareComponent = (fareComponents) =>
      fareComponents.indexOf(BASE_FARE) > -1 ? BASE_FARE : fareComponents[0];

   const getDefaultTripType = (tripTypes) => [
      tripTypes.indexOf(DEFAULT_FARE_TYPE) > -1
         ? DEFAULT_FARE_TYPE
         : tripTypes[0],
   ];

   const handleSetCommonFilters = (availableFilters, categorySummaryKey) => {
      const combinedFilters = combineFiltersOfAllCategories(
         availableFilters,
         categorySummaryKey
      );
      setFilterByPerCategorySummary({
         ...filterByPerCategorySummary,
         [categorySummaryKey]: availableFilters,
      });
      setIsShowSearchLoader(0);
      setCommonFilters(combinedFilters);
      const fareComponents = sortBy(
         combinedFilters[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_COMPONENT]
      );
      const tripTypes = sortBy(
         combinedFilters[DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_TYPE]
      );

      if (!isEmpty(combinedFilters)) {
         setFilterBy({
            ...combinedFilters,
            [DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_COMPONENT]:
               getDefaultFareComponent(fareComponents),
            [DASHBOARD_SEARCH_CRITERIA_KEYS.CRITERIA_FARE_TYPE]:
               getDefaultTripType(tripTypes),
         });
      }
   };

   const handleSetIsShowNotification = (isTrue) => {
      setIsShowNotification(isTrue);
      if (isShowSearchLoader !== 0) {
         setIsShowSearchLoader(
            isShowSearchLoader === LOADER_THRESHOLD + 1
               ? 0
               : isShowSearchLoader + 1
         );
      }
   };

   const handlePosOnClick = (category, pos, reasonsDropdownData) => {
      setFilterBy({ ...filterBy, pos: [pos] });
      setReasonsFilter({ [category]: reasonsDropdownData });
   };

   useEffect(() => {
      fetchAnalystData({ method: 'GET', url: URL_HEP_ANALYST_POS_MAPPING });
   }, []);

   useEffect(() => {
      App.foAnalystApprover = analystData.data.hepList;
   }, [analystData]);

   return (
      <>
         {isShowSearchLoader >= LOADER_THRESHOLD ? (
            <Loader className='dashboard-search' size={SIZE.MEDIUM} />
         ) : null}

         <div className='row spark-mar-t-1 dashboard-top-toolbar'>
            <Tooltip
               anchorX='top'
               toggleEl={
                  <Icon
                     name='search'
                     style='fill'
                     size={SIZE.MEDIUM}
                     label='Search Criteria'
                     onClick={() => handleSearchCriteriaIconOnClick(true)}
                  />
               }
            >
               Search Criteria
            </Tooltip>
         </div>

         <SearchCriteriaModal
            appConfigs={App}
            isOpen={isOpenModal}
            isNotification={isShowNotification}
            isErrorDateOutOfRange={isErrorDateOutOfRange}
            setIsErrorDateOutOfRange={setIsErrorDateOutOfRange}
            recommCompetitor={recommCompetitor}
            searchCriteriaButtonOnClickHandler={
               handleSearchCriteriaButtonOnClick
            }
            searchCriteriaIconOnClickHandler={handleSearchCriteriaIconOnClick}
         />

         <LayoutTwoVerticalGrids
            isExpanded={true}
            leftComponent={
               isEmpty(commonFilters) ? null : (
                  <SearchCriteriaFilters
                     appConfigs={App}
                     filterBy={filterBy}
                     commonFilters={commonFilters}
                     recommCompetitor={recommCompetitor}
                     dashboardFiltersOnChangeHandler={
                        handleDashboardFiltersOnChange
                     }
                  />
               )
            }
            rightComponent={
               <Panel>
                  <DashboardTopTabs
                     dashboardTopTabDetails={
                        dashboardTabConfig.dashboardTopTabDetails
                     }
                     dashboardTopTabOnClickHandler={
                        handleDashboardTopTabOnClick
                     }
                  />

                  {recommCompetitor ===
                  DASHBOARD_TOP_TABS_VALUE.RECOMMENDATION ? (
                     <RecommendationContainer
                        searchCriteriaPayload={searchCriteriaPayload}
                        recommCompetitor={recommCompetitor}
                        setIsOpenSearchModalHandler={
                           handleSearchModalOpenOrClose
                        }
                        setIsShowNotification={handleSetIsShowNotification}
                        filterBy={filterBy}
                        onChangeFilterName={onChangeFilterName}
                        setCommonFiltersHandler={handleSetCommonFilters}
                        posOnClickHandler={handlePosOnClick}
                        reasonsFilter={reasonsFilter}
                        resetReasonFiltersHandler={setReasonsFilter}
                     />
                  ) : (
                     <CompetitionContainer
                        searchCriteriaPayload={searchCriteriaPayload}
                        recommCompetitor={recommCompetitor}
                        setIsOpenSearchModalHandler={
                           handleSearchModalOpenOrClose
                        }
                        setIsShowNotification={handleSetIsShowNotification}
                        filterBy={filterBy}
                        onChangeFilterName={onChangeFilterName}
                        isFareChangesSummaryFromAthena={isFareChangesSummaryFromAthena()}
                        setCommonFiltersHandler={handleSetCommonFilters}
                     />
                  )}
               </Panel>
            }
         />
      </>
   );
};

Dashboard.propTypes = {
   App: object,
};

export default Dashboard;
